import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { calculerJoursOuvres } from '@/lib/heures-utils';

// Types pour le retrait bulk
export interface RetraitData {
  membreId: string;
  affectationId: string;
  dateDebut: string; // Date de début de l'affectation (pour recalcul)
  dernierJour: string; // Dernier jour travaillé
  heuresDernierJour: number; // Heures travaillées le dernier jour
}

export interface RetraitBulkResult {
  success: boolean;
  updated: number;
  failed: number;
  errors: { membreId: string; error: string }[];
}

/**
 * Hook pour gérer le retrait bulk de membres de leurs chantiers
 * Met à jour date_fin, recalcule jours_travailles, et met à jour le statut
 */
export function useRetraitChantierBulk() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  /**
   * Retire plusieurs membres de leurs chantiers
   * @param retraits Liste des retraits à effectuer
   * @param nouveauStatut Statut à appliquer après retrait ('disponible', 'repos', 'en_arret')
   */
  const retirerMembres = async (
    retraits: RetraitData[],
    nouveauStatut: 'disponible' | 'repos' | 'en_arret'
  ): Promise<RetraitBulkResult> => {
    setIsLoading(true);

    const result: RetraitBulkResult = {
      success: true,
      updated: 0,
      failed: 0,
      errors: [],
    };

    try {
      // 1. Mettre à jour chaque affectation individuellement
      for (const retrait of retraits) {
        try {
          // Recalculer les jours ouvrés basé sur la nouvelle date_fin
          const joursOuvres = calculerJoursOuvres(retrait.dateDebut, retrait.dernierJour);

          const { error } = await supabase
            .from('affectations_chantiers')
            .update({
              date_fin: retrait.dernierJour,
              jours_travailles: joursOuvres,
              heures_dernier_jour: retrait.heuresDernierJour,
            })
            .eq('id', retrait.affectationId);

          if (error) {
            throw error;
          }

          result.updated++;
        } catch (err: any) {
          result.failed++;
          result.errors.push({
            membreId: retrait.membreId,
            error: err.message || 'Erreur lors de la mise à jour de l\'affectation',
          });
        }
      }

      // 2. Mettre à jour les statuts des membres (seulement ceux qui ont réussi)
      const membresReussis = retraits
        .filter((r) => !result.errors.find((e) => e.membreId === r.membreId))
        .map((r) => r.membreId);

      if (membresReussis.length > 0) {
        const { error: statutError } = await supabase
          .from('membres_equipe')
          .update({ statut: nouveauStatut })
          .in('id', membresReussis);

        if (statutError) {
          console.error('Erreur mise à jour statuts:', statutError);
          // On ne fail pas tout le batch pour ça, les triggers devraient gérer
        }
      }

      // 3. Afficher le résultat
      if (result.failed === 0) {
        toast({
          title: 'Retrait effectué',
          description: `${result.updated} membre${result.updated > 1 ? 's ont été retirés' : ' a été retiré'} de leur${result.updated > 1 ? 's' : ''} chantier${result.updated > 1 ? 's' : ''}.`,
        });
      } else if (result.updated > 0) {
        toast({
          title: 'Retrait partiel',
          description: `${result.updated} réussi${result.updated > 1 ? 's' : ''}, ${result.failed} en erreur.`,
          variant: 'destructive',
        });
        result.success = false;
      } else {
        toast({
          title: 'Erreur',
          description: 'Impossible de retirer les membres des chantiers.',
          variant: 'destructive',
        });
        result.success = false;
      }

      return result;
    } catch (err: any) {
      console.error('Erreur retrait bulk:', err);
      toast({
        title: 'Erreur',
        description: err.message || 'Une erreur est survenue lors du retrait.',
        variant: 'destructive',
      });
      return {
        success: false,
        updated: 0,
        failed: retraits.length,
        errors: [{ membreId: 'all', error: err.message }],
      };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    retirerMembres,
    isLoading,
  };
}
